/**
 * The signed-in session, in the browser (docs/auth/console-identity-and-access.md
 * section 6.4).
 *
 * The access token lives in memory, here, and nowhere else: not in
 * sessionStorage, not in localStorage, not in a cookie a script can read. The
 * refresh token never reaches the browser at all - the server keeps it sealed
 * under the HttpOnly session cookie (IP-75) - so a reload resumes by asking the
 * server for a fresh access token (/api/auth/refresh), which the cookie makes
 * possible and nothing in page storage does.
 *
 * Refresh happens in the background: REFRESH_AHEAD_SECONDS before the token
 * lapses, and at once when a call is answered 401. Only one refresh is in
 * flight at a time, however many callers are waiting. When a refresh is
 * refused - the session was idle, reached its hour, or was ended - the
 * session is over, and onEnded says why.
 */

/** Refresh this long before the access token lapses. */
export const REFRESH_AHEAD_SECONDS = 60;

/** A JWT's payload - for the expiry, never for a decision the server has not made. */
export function jwtPayload(token) {
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = part + '='.repeat((4 - (part.length % 4)) % 4);
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

/**
 * @param {{
 *   refreshCall: () => Promise<{ accessToken: string, idleSeconds?: number, absoluteExpiresAt?: string }>,
 *   onEnded: (code: string, message: string) => void,
 *   now?: () => number,                                  epoch milliseconds
 *   setTimer?: typeof setTimeout, clearTimer?: typeof clearTimeout,
 * }} options
 */
export function createSession({ refreshCall, onEnded, now = () => Date.now(), setTimer = setTimeout, clearTimer = clearTimeout }) {
  let token = null;
  let limits = null;
  let timer = null;
  let inFlight = null;
  let ended = false;

  const expiresAtMs = (t) => (jwtPayload(t)?.exp ?? 0) * 1000;

  function schedule() {
    if (timer) clearTimer(timer);
    timer = null;
    if (!token) return;
    const wait = Math.max(0, expiresAtMs(token) - REFRESH_AHEAD_SECONDS * 1000 - now());
    timer = setTimer(() => {
      refresh().catch(() => {});
    }, wait);
  }

  function apply(answer) {
    token = answer.accessToken;
    if (answer.absoluteExpiresAt) limits = { idleSeconds: answer.idleSeconds ?? limits?.idleSeconds, absoluteExpiresAt: answer.absoluteExpiresAt };
    ended = false;
    schedule();
  }

  function end(code, message) {
    const wasLive = token !== null;
    token = null;
    limits = null;
    if (timer) clearTimer(timer);
    timer = null;
    if (!ended && wasLive) onEnded(code, message);
    ended = true;
  }

  /**
   * A fresh access token from the session cookie. Resolves true on success;
   * false when there is no session (the cookie is gone or the server refused
   * it) - after which the session is over. One call in flight at a time.
   */
  function refresh() {
    inFlight ??= refreshCall()
      .then((answer) => {
        apply(answer);
        return true;
      })
      .catch((error) => {
        // A network fault is not the end of a session: the next call tries again.
        if (error?.status === 401 || error?.status === 403) {
          end(error.code ?? 'SESSION_UNKNOWN', error.message ?? 'Your session has ended.');
          return false;
        }
        throw error;
      })
      .finally(() => {
        inFlight = null;
      });
    return inFlight;
  }

  return {
    /** A completed sign-in's answer: the token and the session's limits. */
    begin(answer) {
      ended = false;
      apply(answer);
    },
    /** Resume after a reload: true when the cookie still names a live session. */
    async resume() {
      try {
        return await refresh();
      } catch {
        return false;
      }
    },
    refresh,
    /** The current token, refreshed first when it is about to lapse; null when signed out. */
    async accessToken() {
      if (!token) return null;
      if (expiresAtMs(token) - now() > REFRESH_AHEAD_SECONDS * 1000) return token;
      return (await refresh()) ? token : null;
    },
    /** The session is over (signed out, or ended by the server): forget the token. */
    end,
    limits: () => limits,
    isSignedIn: () => token !== null,
  };
}
